import React from "react";
import { Link } from "react-router-dom";
import logo from "assets/images/logo.png";

// Page publique — exigée par Google (écran de consentement OAuth / vérification
// Google Ads API) et par les autres plateformes publicitaires connectées
// (Meta, TikTok, Snapchat, LinkedIn). Doit rester accessible sans compte.
const LAST_UPDATED = "28 juillet 2026";
const CONTACT_EMAIL = "contact@onebotads.net";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-bold text-[#221C51] mb-2">{title}</h2>
    <div className="text-sm text-gray-600 leading-relaxed space-y-3">{children}</div>
  </section>
);

const PrivacyPolicy = () => (
  <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
    <div className="border-b border-gray-100 bg-white">
      <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2">
          <img src={logo} alt="OneBotAds" className="h-7" />
        </Link>
        <Link to="/" className="text-sm text-gray-500 hover:text-[#221C51]">← Retour à l'accueil</Link>
      </div>
    </div>

    <div className="max-w-4xl mx-auto px-6 py-14">
      <h1 className="text-3xl md:text-4xl font-bold text-[#221C51] mb-2">Politique de confidentialité</h1>
      <p className="text-sm text-gray-400 mb-10">Dernière mise à jour : {LAST_UPDATED}</p>

      <Section title="1. Qui nous sommes">
        <p>
          OneBotAds ("nous", "notre", "l'application") est une plateforme qui permet de créer, gérer,
          suivre et automatiser des campagnes publicitaires sur plusieurs régies (Meta/Facebook/Instagram,
          Google Ads, TikTok Ads, Snapchat Ads, LinkedIn Ads) ainsi que des produits Shopify, depuis une
          seule interface. Cette politique décrit quelles données nous collectons, pourquoi, et comment
          vous pouvez les contrôler.
        </p>
      </Section>

      <Section title="2. Données que nous collectons">
        <p><strong>Compte :</strong> nom, adresse e-mail, mot de passe (haché), langue, préférences.</p>
        <p>
          <strong>Comptes publicitaires connectés (OAuth) :</strong> lorsque vous connectez un compte Meta,
          Google Ads, TikTok, Snapchat ou LinkedIn, nous recevons et stockons un jeton d'accès (access
          token / refresh token) permettant d'agir en votre nom sur cette plateforme (lire et créer des
          campagnes, groupes d'annonces, annonces, pixels), ainsi que les identifiants de vos comptes
          publicitaires et pages/identités associées.
        </p>
        <p>
          <strong>Données de campagne :</strong> campagnes, groupes d'annonces, annonces, budgets, audiences
          ciblées (localisation, âge, genre, langues), objectifs, statistiques de performance, que vous
          créez via l'application ou qui existent déjà sur vos comptes connectés.
        </p>
        <p>
          <strong>Créatifs générés par IA :</strong> images, vidéos et voix off générées à votre demande,
          les prompts utilisés, et le modèle IA sollicité (Google Imagen/Veo/Gemini, ByteDance Seedance,
          Higgsfield, Replicate, Hugging Face).
        </p>
        <p>
          <strong>Données de tracking :</strong> si vous configurez notre tracking/pixel, nous recevons des
          événements de conversion (visite, achat, etc.) transmis par votre site ou boutique afin de les
          relayer aux régies publicitaires connectées (API de conversions Meta/TikTok/Snapchat).
        </p>
        <p><strong>Données techniques :</strong> adresse IP, type de navigateur, pages visitées, journaux d'erreurs.</p>
        <p><strong>Paiement :</strong> les informations de paiement (carte bancaire) sont traitées par notre prestataire de paiement ; nous ne stockons jamais votre numéro de carte complet sur nos serveurs.</p>
      </Section>

      <Section title="3. Comment nous utilisons ces données">
        <ul className="list-disc pl-5 space-y-1">
          <li>Fournir le service : créer et gérer vos campagnes sur les plateformes que vous connectez.</li>
          <li>Générer des créatifs publicitaires (image, vidéo, voix) à votre demande.</li>
          <li>Afficher vos statistiques de performance et votre historique de campagnes.</li>
          <li>Facturer votre abonnement et vos crédits d'utilisation.</li>
          <li>Assurer la sécurité du compte et prévenir la fraude/l'abus.</li>
          <li>Vous contacter au sujet du service (notifications, support, mises à jour importantes).</li>
          <li>Améliorer l'application (statistiques d'usage agrégées et anonymisées).</li>
        </ul>
      </Section>

      <Section title="4. Utilisation des API Google et politique des données utilisateur">
        <p>
          L'utilisation par OneBotAds des informations reçues des API Google et transmises à d'autres
          applications respectera la{" "}
          <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer" className="text-[#221C51] underline">
            Politique relative aux données utilisateur des services API Google
          </a>, y compris les exigences de Limited Use. Les données obtenues via Google Ads API (comptes,
          campagnes, statistiques) sont utilisées exclusivement pour fournir et améliorer les
          fonctionnalités de gestion publicitaire d'OneBotAds que vous avez explicitement demandées ; elles
          ne sont ni vendues, ni utilisées à des fins publicitaires tierces, ni transférées à des fins non
          liées au service sans votre consentement explicite.
        </p>
      </Section>

      <Section title="5. Partage avec des tiers">
        <p>Nous partageons des données uniquement dans les cas suivants :</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Régies publicitaires connectées</strong> (Meta, Google Ads, TikTok, Snapchat, LinkedIn) : pour exécuter les actions que vous demandez (créer/modifier une campagne, envoyer un événement de conversion).</li>
          <li><strong>Prestataires IA génératifs</strong> (Google Gemini/Imagen/Veo, ByteDance/ModelArk Seedance, Higgsfield, Replicate, Hugging Face, Groq) : le texte de vos prompts et, le cas échéant, une image source, pour générer le contenu demandé.</li>
          <li><strong>Hébergement média</strong> (Cloudinary) : pour stocker et servir les images/vidéos générées ou importées.</li>
          <li><strong>Prestataire de paiement</strong> : pour traiter les abonnements et achats de crédits.</li>
          <li><strong>Hébergeur applicatif</strong> : pour faire fonctionner nos serveurs.</li>
          <li><strong>Obligations légales</strong> : si la loi l'exige.</li>
        </ul>
        <p>Nous ne vendons jamais vos données personnelles à des tiers.</p>
      </Section>

      <Section title="6. Conservation des données">
        <p>
          Vos données sont conservées tant que votre compte est actif. Les jetons OAuth sont révoqués et
          supprimés dès que vous déconnectez une plateforme. Vous pouvez demander la suppression complète
          de votre compte et des données associées à tout moment (voir section 8).
        </p>
      </Section>

      <Section title="7. Sécurité">
        <p>
          Les mots de passe sont hachés, les connexions au site sont chiffrées (HTTPS/TLS), et l'accès aux
          jetons OAuth est restreint aux traitements nécessaires au fonctionnement du service. Aucune
          transmission sur Internet n'étant garantie sûre à 100 %, nous ne pouvons pas garantir une
          sécurité absolue, mais nous appliquons les pratiques standard de l'industrie.
        </p>
      </Section>

      <Section title="8. Vos droits">
        <p>
          Conformément au RGPD (utilisateurs de l'UE) et aux lois applicables, vous disposez d'un droit
          d'accès, de rectification, d'effacement, de portabilité et d'opposition sur vos données
          personnelles. Vous pouvez révoquer l'accès OAuth d'une plateforme à tout moment depuis
          "Plateformes connectées" dans l'application, ou directement depuis les paramètres de votre compte
          Google/Meta/TikTok/Snapchat/LinkedIn. Pour toute autre demande, contactez-nous à{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#221C51] underline">{CONTACT_EMAIL}</a>.
        </p>
      </Section>

      <Section title="9. Cookies">
        <p>
          Nous utilisons des cookies strictement nécessaires (session de connexion) et, le cas échéant, des
          cookies de mesure d'audience. Vous pouvez configurer votre navigateur pour refuser les cookies non
          essentiels.
        </p>
      </Section>

      <Section title="10. Mineurs">
        <p>OneBotAds ne s'adresse pas aux personnes de moins de 18 ans et ne collecte pas sciemment de données les concernant.</p>
      </Section>

      <Section title="11. Modifications de cette politique">
        <p>
          Nous pouvons mettre à jour cette politique. Toute modification substantielle vous sera notifiée
          par e-mail ou via l'application. La date de dernière mise à jour figure en haut de cette page.
        </p>
      </Section>

      <Section title="12. Contact">
        <p>
          Pour toute question relative à cette politique ou à vos données : {" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#221C51] underline">{CONTACT_EMAIL}</a>.
        </p>
      </Section>
    </div>
  </div>
);

export default PrivacyPolicy;
